import { withTranslation } from "react-i18next";
import WithRouter from "../../../../components/Common/WithRouter";
import React, { useRef, useState } from "react";
import Tooltip from "../../../../components/Common/Display/Tooltip";
import StateLessGrid from "../../../../components/Shared/StateLessGrid";
import { Button } from "primereact/button";
import { Col, Row } from "react-bootstrap";
import { Field, Form, Formik, FormikProps } from "formik";
import { InputField } from "../../../../components/Shared/InputField";
import { includes, isEmpty, uniqBy } from "lodash";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import VinWithType from "../../../../components/Common/Display/VinWithType";
import Location from "../../../../components/Common/Display/Location";
import OrderTransportRequestFilter from "./OrderTransportRequestFilter";
import { utcDateToLocalDate } from "../../../../utils/Helper";
import OrderTransportRequestDetail from "./OrderTransportRequestDetail";
import AlpOutboundLoadStatus from "../../alpOutboundLoads/partials/AlpOutboundLoadStatus";
import { ButtonGroup } from "primereact/buttongroup";
import { Dialog } from "primereact/dialog";
import OrderTransportRequestExcel from "./OrderTransportRequestExcel";
import { request } from "../../../../utils/Request";
import AvisoOutboundLoadForm from "./AvisoOutboundLoadForm";
import HasAccess from "../../../../utils/HasAccess";
import qs from "qs";
import AlpOutboundLoadDetail from "../../alpOutboundLoads/AlpOutboundLoadDetail";
import AlpFreezeStatusNotice from "../../loads/partials/AlpFreezeStatusNotice";
import QuickCopyDropdown from "../../../../components/Shared/QuickCopyDropdown";
import LoadComments from "../../loads/partials/LoadComments";
import OrderLabel from "../../../../components/Common/Display/OrderLabel";

const OrderTransportRequestList = (props: any) => {
    const { t, moduleState, toastify } = props;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [operation, setOperation] = useState<any>(searchParams.get('operation') ?? null);
    const [operationId, setOperationId] = useState<any>(searchParams.get('operationId') ?? null);
    const [filterPanel, setFilterPanel] = useState<boolean>(false);
    const [excelPanel, setExcelPanel] = useState<boolean>(false);
    const [initSearchFormState] = useState<any>({ search: null });
    const [total, setTotal] = useState(0);
    const [orderLegendModal, setOrderLegendModal] = useState<boolean>(false);
    const tableRef: any = useRef();
    const [clickCount, setClickCount] = useState(0);
    const [visibleSpecialAction, setVisibleSpecialAction] = useState(false);
    const [syncOnOrderTransportRequests, setSyncOnOrderTransportRequests] = useState(false);
    const [disableSpecialActionOrderTransportRequests, setDisableSpecialActionOrderTransportRequests] = useState(false);
    const [selectedOrders, setSelectedOrders] = useState<any[]>([]);
    const [avisoOutboundLoadPanel, setAvisoOutboundLoadPanel] = useState<boolean>(false);
    const [avisoSourceType, setAvisoSourceType] = useState<'emil_frey' | 'vehicle_logistics'>('emil_frey');
    const [selectAllLoading, setSelectAllLoading] = useState(false);
    const [commentLoad, setCommentLoad] = useState<any>(null);

    const normalizeSourceType = (row: any): 'emil_frey' | 'vehicle_logistics' => {
        return (row?.source_type === 'vehicle_logistics')
            ? 'vehicle_logistics'
            : 'emil_frey';
    };

    const isEligibleForAvisoGroup = (row: any) => {
        const status = ( row?.load_status || '' ).toString().toLowerCase();
        return row?.source === 'alp_outbound_load_order' && status === 'announced';
    };

    const refreshTable = () => {
        if ( tableRef.current ) {
            tableRef.current.onRefresh();
        }
    };

    const onOpenLoadComments = (row: any) => setCommentLoad({
        ...row,
        type: row.type || 'AlpOutboundLoad',
        load_source_id: row.load_source_id || row.alp_outbound_load_guid || row.id,
    });

    const handleHeaderClick = () => {
        const newCount = clickCount + 1;
        setClickCount(newCount);

        if ( newCount >= 5 && !visibleSpecialAction ) {
            setVisibleSpecialAction(true);
        }
    };

    const onOrderTransportRequestSyncList = async () => {
        setSyncOnOrderTransportRequests(true);
        try {
            await request({
                url: '/suppliers/provider/orders/transport/requests/sync/list',
                method: 'POST',
            });

            toastify(t("request received to sync all transport request"), "info");
            refreshTable();
        } catch (e: any) {
            if ( e.status === 422 ) {
                toastify(t("unprocessable entity"), "error");
            } else if ( e.statusText ) {
                toastify(e.statusText, "error");
            } else {
                toastify(t("server error"), "error");
            }
        } finally {
            setSyncOnOrderTransportRequests(false);
            setDisableSpecialActionOrderTransportRequests(true);
        }
    };

    const onSelectAllClick = async () => {
        try {
            setSelectAllLoading(true);

            const params: any = { ...( moduleState || {} ) };
            delete params.page;
            delete params.per_page;

            const response = await request({
                url: '/suppliers/orders/transport/requests',
                params,
                paramsSerializer: {
                    serialize: (p: any) => qs.stringify(p)
                },
            });

            const allRows: any[] = ( response?.data?.data || [] );

            const eligible = ( allRows || [] ).filter(isEligibleForAvisoGroup);
            const ineligibleCount = ( allRows || [] ).length - eligible.length;

            if ( ( allRows || [] ).length <= 0 ) {
                toastify(t("no records found"), "info");
                setSelectedOrders([]);
                // @ts-ignore
                tableRef.current?.clearAllSelection?.();
                return;
            }

            if ( eligible.length <= 0 ) {
                toastify(t("only announced loads can be added to load"), "info");
                setSelectedOrders([]);
                // @ts-ignore
                tableRef.current?.clearAllSelection?.();
                return;
            }

            if ( ineligibleCount > 0 ) {
                toastify(t("some selected rows are not eligible and were skipped"), "info");
            }

            setSelectedOrders(eligible);

            // Select only currently visible eligible rows in the table UI.
            // Full selection still lives in `selectedOrders`.
            const currentEntities: any[] = (
                // @ts-ignore
                tableRef.current?.getEntities?.() || []
            );
            const eligibleRowIds = new Set(eligible.map((o: any) => o.row_id ?? o.id));
            const visibleEligible = ( currentEntities || [] ).filter((r: any) => eligibleRowIds.has(r.row_id ?? r.id));

            // @ts-ignore
            tableRef.current?.setSelection?.(visibleEligible);
        } catch (e: any) {
            toastify(t("server error"), "error");
        } finally {
            setSelectAllLoading(false);
        }
    };

    const columns = [
        {
            field: 'brand',
            title: t("brand") + '/' + t("model"),
            sorting: true,
            sortField: "brand_id",
            render: ({ brand, brand_model }: any) => <div className={ "text-capitalize" }>
                { brand?.title }
                { brand?.alternate_title && <div> { brand.alternate_title }</div> }
                <div>
                    { brand_model?.title }
                    { brand_model?.version_code && <div> { brand_model.version_code } </div> }
                </div>
            </div>
        },
        {
            field: 'client',
            title: t("client"),
            sorting: true,
            sortField: "client_id",
            render: ({ client }: any) => <div className={ "text-capitalize" }>
                { client?.name }
            </div>
        },
        {
            field: 'provider',
            title: t("provider"),
            sorting: true,
            sortField: "provider_id",
            render: (_row: any) => <div className={ "text-capitalize" }>
                { _row?.provider?.title || _row?.source_type || "-" }
            </div>
        },
        {
            field: 'vin_number',
            title: t('vin number'),
            sorting: true,
            sortField: "vin_number",
            render: (_row: any) => (
                <>
                    <VinWithType vinNumber={ _row.vin_number } type={ _row.source_type }
                                 priorisation={ _row.priorisation } builtToOrderCd={ _row.built_to_order_cd } />
                </>
            )
        },
        { field: 'order_cd', title: t('order'), sortField: "order_cd", sorting: true },
        { field: 'ldg_nr', title: t('ldg nr'), sortField: "ldg_nr", sorting: true },
        {
            field: 'load_number',
            title: t('load number'),
            sorting: false,
            render: (_row: any) => {
                return _row?.load_number ? `${ _row.load_number }` : "-";
            }
        },
        {
            field: 'status',
            title: t('status'),
            sorting: true,
            sortField: "status",
            render: (_row: any) => (
                <div className="text-capitalize">
                    <AlpOutboundLoadStatus status={ _row.load_status || _row.status } />
                    <OrderLabel labels={ _row.order_labels } className="mt-1" />
                </div>
            )
        },
        {
            field: 'emil_frey_supplier_compound',
            title: t("supplier compound"),
            sorting: true,
            sortField: "emil_frey_supplier_compound"
        },
        {
            field: 'emil_frey_dealer',
            title: t("dealer"),
            sorting: true,
            sortField: "emil_frey_dealer"
        },
        {
            field: 'from_location',
            sorting: false,
            title: t('from location'),
            render: (_row: any) => {
                const { current_journey } = _row;
                return (
                    <>
                        {
                            ( current_journey && current_journey.from_location ) &&
                          <>
                              { current_journey.from_location.street_line_1 &&
                                <small className={ "d-block" }>{ current_journey.from_location.street_line_1 }</small> }
                              <Location item={ current_journey.from_location } titled={ 1 } />
                          </>
                        }
                    </>
                );
            }
        },
        {
            field: 'to_location',
            sorting: false,
            title: t('to location'),
            render: (_row: any) => {
                const { current_journey } = _row;
                return (
                    <>
                        {
                            ( current_journey && current_journey.to_location ) &&
                          <>
                              { current_journey.to_location.street_line_1 &&
                                <small className={ "d-block" }>{ current_journey.to_location.street_line_1 }</small> }
                              <Location item={ current_journey.to_location } titled={ 1 } />
                          </>
                        }
                    </>
                );
            }
        },
        {
            field: 'created_at',
            title: t('created at'),
            sorting: false,
            render: (_row: any) => {
                const loadCreatedAt = _row?.load_created_at;
                return ( loadCreatedAt
                        ? <Tooltip
                            tooltip={ utcDateToLocalDate(loadCreatedAt) }>{ utcDateToLocalDate(loadCreatedAt) }</Tooltip>
                        : "-"
                );
            },
        },
        {
            field: 'delivery_request_date',
            title: t('delivery request date'),
            sorting: false,
            render: (_row: any) => {
                const deliveryRequestDate = _row?.alp_created_at || _row?.load_created_at;
                return deliveryRequestDate
                    ? <Tooltip tooltip={ utcDateToLocalDate(deliveryRequestDate) }>{ utcDateToLocalDate(deliveryRequestDate) }</Tooltip>
                    : "-";
            },
        },
        {
            field: 'comments',
            title: t('comments'),
            sorting: false,
            align: 'center',
            render: (row: any) => {
                if ( !(row.load_source_id || row.alp_outbound_load_guid) ) {
                    return null;
                }

                const count = Number(row?.load_comments_count || 0);

                return (
                    <HasAccess hasAnyAccess={ ['loadcommentsview', 'loadcommentsstore'] }>
                        {/*@ts-ignore*/}
                        <Tooltip tooltip={ count > 0 ? `${ t('comments') } (${ count })` : t('comments') }>
                            <a onClick={ () => onOpenLoadComments(row) }
                               className={ "cursor-pointer ms-1 text-blue-500 load-comment-action" }>
                                <i className="fas fa-comments" />
                                { count > 0 &&
                                  <span className="load-comment-action__count">{ count }</span>
                                }
                            </a>
                        </Tooltip>
                    </HasAccess>
                );
            },
        },
        {
            field: 'action',
            title: t('action'),
            sorting: false,
            align: 'right',
            render: (_row: any) => (
                <Tooltip tooltip={ t('detail of item') }>
                    <a
                        className={ 'ms-2 cursor-pointer' }
                        onClick={ () => {
                            if ( !_row?.alp_outbound_load_guid ) {
                                return;
                            }
                            onOperationClick('alp_outbound_load_detail', _row.alp_outbound_load_guid);
                        } }
                    >
                        <i className="fas fa-eye" />
                    </a>
                </Tooltip>
            )
        },
    ];

    const onOperationClick = (operationToBePerformed: any = 'detail', id: any = null) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('operation', operationToBePerformed);
        searchParams.set('operationId', id);

        if ( isEmpty(operationToBePerformed) ) {
            searchParams.delete('operation');
        }

        if ( isEmpty(id) ) {
            searchParams.delete('operationId');
        }

        navigate({
            pathname: location.pathname,
            search: searchParams.toString(),
        }, { replace: true });
        setOperationId(id);
        setOperation(operationToBePerformed);
    };

    const onCloseOperation = (reset: any = true) => {
        onOperationClick(null, null);
        onReset(reset);
        focusOnSearch();
    };

    const onReset = (reset: any = true) => {
        const mStates = { ...moduleState };

        if ( reset ) {
            mStates.page = 1;
            mStates.search = null;
        }

        props.setQuery(mStates);
    };

    const onSearch = ({ search }: any) => {
        props.setQuery({
            ...moduleState,
            search: search
        });
    };

    const onFilterClick = () => {
        setFilterPanel(true);
    };

    const onExcelClick = () => {
        setExcelPanel(true);
    };

    const onClickOpenOrderLegends = () => {
        setOrderLegendModal(true);
    };

    const onOpenAvisoOutboundLoad = (sourceType: 'emil_frey' | 'vehicle_logistics') => {
        if ( isEmpty(selectedOrders) ) {
            toastify(t("please select orders first"), "info");
            return;
        }

        const eligible = ( selectedOrders || [] )
            .filter(isEligibleForAvisoGroup)
            .filter((row: any) => normalizeSourceType(row) === sourceType);

        const ineligibleCount = ( selectedOrders || [] ).length - eligible.length;

        if ( eligible.length <= 0 ) {
            toastify(t("please select announced loads"), "info");
            return;
        }

        if ( ineligibleCount > 0 ) {
            toastify(t("some selected rows are not eligible and were skipped"), "info");
            setSelectedOrders(eligible);
        }

        setAvisoSourceType(sourceType);
        setAvisoOutboundLoadPanel(true);
    };

    const onRemoveSelectedOrder = (orderId: any) => {
        setSelectedOrders((prev: any[]) => prev.filter((o: any) => ( o.row_id ?? o.id ) !== orderId));
    };

    const onClearAllSelectedOrders = () => {
        setSelectedOrders([]);
    };

    const focusOnSearch = () => {
        const searchInput: HTMLInputElement | null = document.querySelector('input[name="search"]');
        if ( searchInput ) {
            searchInput.focus();
        }
    };

    return (
        <div className={ "block-viewer" }>
            <div className="block-section">
                <div className="block-header">
                    <span className="block-title">
                        <h5 className={ "mb-0" }>
                            <span onClick={ handleHeaderClick }>
                                { t('order transport requests') } ({ total })
                            </span>
                        </h5>
                    </span>
                    <div className="block-actions">
                        {
                            ( operation === 'detail' || operation === 'alp_outbound_load_detail' ) &&
                          <div>
                              <Button type={ "button" }
                                      tooltip={ t("operation cancel") } className={ "ms-2" }
                                      size={ "small" } severity={ "warning" }
                                      tooltipOptions={ { position: 'top' } } onClick={ () => onCloseOperation(false) }>
                                  <i className="pi pi-arrow-left me-2" /> { t("cancel") }

                              </Button>
                          </div>
                        }
                        {
                            !operation && <AlpFreezeStatusNotice />
                        }
                    </div>
                </div>
                <div className="block-content">
                    <Row
                        className={ `p-2 ${ ( operation === 'detail' || operation === 'alp_outbound_load_detail' ) ? 'position-minus' : '' }` }>
                        <Col
                            sm={ 12 } md={ 6 } className={ "mb-2" }>
                            <Formik
                                enableReinitialize={ true }
                                initialValues={ initSearchFormState }
                                onSubmit={ onSearch }
                            >
                                { (props: FormikProps<any>) => {
                                    return (
                                        <Form onSubmit={ props.handleSubmit }>
                                            <Col sm={ 12 } md={ 8 }>
                                                <div className={ 'd-flex' }>
                                                    <Field component={ InputField }
                                                           withoutLabel={ true }
                                                           autoFocus={ true }
                                                           name="search"
                                                           formGroupClass={ "mb-0" }
                                                           placeholder={ t("search") } />
                                                    <Tooltip className="ms-2"
                                                             target={ `.order-legends` } />
                                                    <a
                                                        data-pr-tooltip={ `${ t("price legends") }` }
                                                        data-pr-position="left"
                                                        onClick={ onClickOpenOrderLegends }
                                                        className={ "order-legends cursor-pointer" }>
                                                        <i className="fas fa-info-circle text-blue-400" style={ {
                                                            fontSize: '1.2rem',
                                                            marginTop: '0.8rem',
                                                            cursor: 'pointer'
                                                        } } />
                                                    </a>
                                                </div>
                                            </Col>
                                        </Form>
                                    );
                                } }
                            </Formik>
                        </Col>

                        <Col
                            sm={ 12 } md={ 6 } className={ "text-right mb-2" }>
                            <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                                <div className={ "text-left" }>
                                    <QuickCopyDropdown
                                        columns={ columns }
                                        tableName={ "orderTransportRequestList_v2" }
                                        selectedRows={ selectedOrders }
                                        trackBy={ "row_id" }
                                        // @ts-ignore
                                        entities={ tableRef.current?.getEntities?.() || [] }
                                    />
                                </div>
                                <ButtonGroup>
                                    {
                                        ( visibleSpecialAction && !disableSpecialActionOrderTransportRequests ) &&
                                        <Button
                                            icon="pi pi-sync" size={ "small" }
                                            outlined disabled={ syncOnOrderTransportRequests }
                                            severity={ "danger" }
                                            tooltip={ t("sync") }
                                            tooltipOptions={ { position: 'top' } }
                                            onClick={ onOrderTransportRequestSyncList }
                                        />
                                    }
                                    <HasAccess hasAnyAccess={ ['alpgrouploadsstore'] }>
                                        <Button
                                            icon="pi pi-building"
                                            size={ "small" }
                                            severity={ "info" }
                                            disabled={ (selectedOrders || []).filter((row: any) => isEligibleForAvisoGroup(row) && normalizeSourceType(row) === 'emil_frey').length <= 0 }
                                            tooltip={ t("create outbound group for Emil Frey only") }
                                            onClick={ () => onOpenAvisoOutboundLoad('emil_frey') }
                                            tooltipOptions={ { position: 'top' } }
                                        />
                                        <Button
                                            icon="pi pi-sitemap"
                                            size={ "small" }
                                            severity={ "help" }
                                            disabled={ (selectedOrders || []).filter((row: any) => isEligibleForAvisoGroup(row) && normalizeSourceType(row) === 'vehicle_logistics').length <= 0 }
                                            tooltip={ t("create outbound group for Vehicle Logistics only") }
                                            onClick={ () => onOpenAvisoOutboundLoad('vehicle_logistics') }
                                            tooltipOptions={ { position: 'top' } }
                                        />
                                    </HasAccess>
                                    <Button
                                        icon="pi pi-check"
                                        size={ "small" }
                                        outlined
                                        disabled={ selectAllLoading }
                                        tooltip={ t("select all") }
                                        onClick={ onSelectAllClick }
                                        tooltipOptions={ { position: 'top' } }
                                    />
                                    <Button icon="pi pi-filter-fill" size={ "small" } outlined tooltip={ t('filter') }
                                            onClick={ onFilterClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button icon="pi pi-file-excel" size={ "small" } outlined tooltip={ t('excel') }
                                            onClick={ onExcelClick }
                                            tooltipOptions={ { position: 'top' } } />
                                    <Button onClick={ onReset } icon="pi pi-refresh" size={ "small" } outlined
                                            tooltip={ t('reset') }
                                            tooltipOptions={ { position: 'top' } } />
                                </ButtonGroup>
                            </div>
                        </Col>

                        <Col sm={ 12 }>
                            <StateLessGrid
                                size={ "normal" }
                                url={ '/suppliers/orders/transport/requests?1=1' }
                                // Bump the key so users see the new columns by default (load_number).
                                name={ "orderTransportRequestList_v2" }
                                columns={ columns }
                                moduleState={ moduleState }
                                setQuery={ props.setQuery }
                                ref={ tableRef }
                                trackBy={ "row_id" }
                                itemSize={ 130 }
                                setTotal={ setTotal }
                                stickyHeader={ true }
                                showQuickCopy={ false }
                                onRowSelection={ (rows: any[]) => {
                                    const eligible = ( rows || [] ).filter(isEligibleForAvisoGroup);
                                    if ( ( rows || [] ).length !== eligible.length ) {
                                        toastify(t("only stocked orders or announced loads can be added to load"), "info");
                                    }

                                    // Merge selection by current page, so "select all" stays intact.
                                    // @ts-ignore
                                    const currentEntities: any[] = ( tableRef.current?.getEntities?.() || [] );
                                    const currentRowIds = new Set(( currentEntities || [] ).map((r: any) => ( r.row_id ?? r.id )));
                                    setSelectedOrders((prev: any[]) => {
                                        const kept = ( prev || [] ).filter((o: any) => !currentRowIds.has(o.row_id ?? o.id));
                                        return uniqBy([...kept, ...eligible], 'row_id');
                                    });
                                } }
                                selectedRows={ selectedOrders }
                            />
                        </Col>
                    </Row>

                    {
                        ( operation === 'detail' && !isEmpty(operationId) ) &&
                      <OrderTransportRequestDetail
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        params={ { operation, operationId } }
                        onClose={ onCloseOperation }
                      />
                    }

                    {
                        ( operation === 'alp_outbound_load_detail' && !isEmpty(operationId) ) &&
                      <AlpOutboundLoadDetail
                        toastify={ props.toastify }
                        params={ { operation: 'detail', operationId } }
                        onClose={ onCloseOperation }
                      />
                    }

                    <OrderTransportRequestFilter
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        visibility={ filterPanel }
                        onClose={ () => {
                            setFilterPanel(false);
                            focusOnSearch();
                        } }
                    />

                    <OrderTransportRequestExcel
                        moduleState={ moduleState }
                        setQuery={ props.setQuery }
                        toastify={ props.toastify }
                        dropdowns={ props.dropdowns }
                        visibility={ excelPanel }
                        onClose={ () => {
                            setExcelPanel(false);
                            focusOnSearch();
                        } }
                    />

                    <AvisoOutboundLoadForm
                        visible={ avisoOutboundLoadPanel }
                        selectedOrders={ selectedOrders }
                        sourceType={ avisoSourceType }
                        onClose={ () => {
                            setAvisoOutboundLoadPanel(false);
                            setSelectedOrders([]);
                            // @ts-ignore
                            tableRef.current?.clearAllSelection?.();
                            refreshTable();
                        } }
                        onRemoveOrder={ onRemoveSelectedOrder }
                        onClearAll={ onClearAllSelectedOrders }
                        toastify={ toastify }
                    />

                    <LoadComments
                        id={ commentLoad ? (commentLoad.load_source_id || commentLoad.source_id || commentLoad.id) : null }
                        load={ commentLoad }
                        toastify={ toastify }
                        onRefresh={ refreshTable }
                        onClose={ () => {
                            setCommentLoad(null);
                            focusOnSearch();
                        } }
                    />

                    <Dialog header={
                        t('order legends')
                    } visible={ orderLegendModal } style={ { width: '50vw' } }
                            onHide={ () => setOrderLegendModal(false) }
                            breakpoints={ { '960px': '85vw', '641px': '100vw' } }>
                        <div>
                            <p className={ "m-0 fs-6" }>
                                <i className="fas fa-circle text-secondary me-1" />
                                { t('journey is finished') }
                            </p>
                            <p className={ "m-0 fs-5 mt-1" }>
                                <i className="fas fa-circle text-success me-1" />
                                { t('active journey') }
                            </p>
                            <p className={ "m-0 fs-5 mt-1" }>
                                <i className="fas fa-circle text-info me-1" />
                                { t('pending is journey') }
                            </p>
                        </div>
                    </Dialog>

                </div>
            </div>
        </div>
    );
};

export default withTranslation()(WithRouter(OrderTransportRequestList));

